import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';

import { useStepper, StepperProvider } from './hooks/useStepper';
import { VaccineValues } from './validation';
import { VaccinationService } from '../../services';
import {
  LocationType,
  UserVaccination,
  VaccinationCampaign,
  VaccinationStatusType,
} from '../../services/models/vaccination';

import Layout from '../../layout';
import Head from '../../components/Head';
import MyVaccine from './MyVaccine';
import VaccineFirstStep from './FirstStep';
import VaccineSecondStep from './SecondStep';
import VaccineThirdStep from './ThirdStep';
import VaccineFourthStep from './FourthStep';
import VaccineFifthStep from './FifthStep';
import OpenVaccine from './OpenVaccine';
import AccreditedNetwork from './AccreditedNetwork';
import Backdrop from '../../components/Backdrop';

function MyHealthVaccine() {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<UserVaccination>({} as UserVaccination);
  const [campaign, setCampaign] = useState<VaccinationCampaign>();
  const [taken, setTaken] = useState<{ scheduledDate: Date }[]>([]);
  const stepper = useStepper<VaccineValues>(5);

  const steps = useMemo(
    () => [
      <VaccineFirstStep vaccination={info} loading={loading} />,
      <VaccineSecondStep campaign={campaign} taken={taken} />,
      <VaccineThirdStep />,
      <VaccineFourthStep info={info} />,
      <VaccineFifthStep
        onStatusChanged={(status) =>
          setInfo((current) => ({ ...current, status }))
        }
      />,
    ],
    [info, campaign, taken, loading]
  );

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const [info, campaign, taken] = await Promise.all([
          VaccinationService.getCurrentUserActiveCampaignInformation(),
          VaccinationService.getActiveCampaign(),
          VaccinationService.getActiveCampaignTaken(),
        ]);

        setInfo(info.data);
        setCampaign(campaign.data);
        setTaken(taken.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }

    fetch();
  }, [info.status]);

  const renderForm = useCallback(() => {
    return steps[stepper.currentPage];
  }, [stepper.currentPage, steps]);

  const handlePage = useCallback(
    (status?: VaccinationStatusType, locationType?: LocationType) => {
      if (locationType === LocationType.AccreditedNetwork) return (
        <Layout
          page="myHealth"
          hideBackButton
          innerPage
        >
          <AccreditedNetwork vaccination={info} />
        </Layout>);
      else if (locationType === LocationType.Refund) return (
        <Layout
          page="myHealth"
          hideBackButton
          innerPage
        >
          <OpenVaccine isRefund />
        </Layout>
      );
      else {
        switch (status) {
          case VaccinationStatusType.Accepted:
          case VaccinationStatusType.Canceled:
            return (
              <Layout
                page="myHealth"
                backLink="/minha-saude"
                hideBackButton={stepper.currentPage !== 0}
                innerPage
              >
                <Box px={2}>
                  <StepperProvider {...stepper}>{renderForm()}</StepperProvider>
                </Box>
              </Layout>
            );
          case VaccinationStatusType.Scheduled:
          case VaccinationStatusType.Attended:
            return (
              <MyVaccine
                Vaccination={info}
                onStatusChanged={(status) =>
                  setInfo((current) => ({ ...current, status }))
                }
              />
            );
          default:
            return (
              <Layout
                page="myHealth"
                hideBackButton
                innerPage
              >
                <OpenVaccine />
              </Layout>
            );
        }
      }
    },
    [renderForm, stepper, info]
  );

  return <>
    <Head>Vacinação</Head>
    <Backdrop loading={loading} />
    {handlePage(info?.status, info.locationType)}
  </>;
}

export default MyHealthVaccine;
